import _ from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import IfmsModal from '../../../../ui/IfmsModal';
import useTableNavigation from '../../../../ui/tables/useTableNavigation';

import ApplicationContext from '../../../../ApplicationContext';
import useGlobalOkDialog from '../../../../hooks/useGlobalOkDialog';
import useSystemSettings from '../../../../hooks/useSystemSettings';
import useFastStorage, { useTypedFastStorage } from '../../../../hooks/useFastStorage';

import { ApplicableTableSettings, codesAirlineList } from '../../../../tableSettings';

import {
  TextAlignLeftRenderer,
  TextRenderer,
  TextAlignRightRenderer,
  DateTimeRenderer,
  FloatRenderer,
} from '../../../../uiControls/SmartTableRenderers';

import SmartTable from '../../../../uiControls/SmartTable';
import SecondaryButton from '../../../../ui/SecondaryButton';
import { Country } from '../../../../ifmsTypes';
import useFunction from '../../../../hooks/useFunction';
import { Search, isFilterObjectEmpty, resetSearchKeepPaging } from '../../../../filtering';
import InputControlRow from '../../../../ui/layout/InputControlRow';
import TextBox from '../../../../ui/TextBox';
import CountryIsoCodeDropDown from '../../../../uiControls/CountryIsoCodeDropDown';
import LeftRightSplit from '../../../../ui/LeftRightSplit';
import DateTime from '../../../../ui/DateTime';
import Float from '../../../../ui/Float';
import CheckBox from '../../../../ui/CheckBox';
import CloseButton from '../../../../ui/CloseButton';
import { getSQLInfoList } from '../../../../fms.core/data/TrackingModule';
import { TrackableEntity } from '../../../../fms.core/data/ChangeTrackable';
import { CodeAirLine } from '../../../../fms.core/CodeAirLine';
import { ApiResult }  from '../../../../framework.core/data/ApiResult';
import { JobNames } from '../../../../framework.core/JobNames';
import { CodeAirlineListItem } from '../../../../fms.data/models/CodeAirlineListItem';
import useDirtyCheck from '../../../../hooks/useDirtyCheck2';
import { now } from '../../../../dateUtils';
import { appendLine, findSecurityTask, stringsEqualIgnoreCase } from '../../../../util';
import { FmsSecTask } from '../../../../fms.core/constants';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';
import useGlobalYesNoDialog from '../../../../hooks/useGlobalYesNoDialog';
import useFilterBar from '../../../../hooks/useFilterBar';
import { ListOrderBy } from "../../../common/AbstractListView";
import postal from 'postal';
import BlackInverseButton from '../../../../ui/BlackInverseButton';
import CompanyListExLookupField from '../../../../uiControls/lookup/CompanyListExLookupField';
import { LookupCompany } from '../../../../fms.data/lookup';
import constants from '../../../../constants';
import IFmsReferenceFilesContext from '../../IfmsReferenceFilesContext';
import { Portal } from 'react-portal';
import { AddIconButton3, DeleteIconButton3, EditIconButton3, ViewIconButton3, RefreshIconButton2 } from '../../../../ui/IconButton';

interface CodeAirLineResult {
  newId : number,
  oldId : number,
  model : string
}

function RowSpacer() {
  return (
    <div style={{height: '10px'}}>&nbsp;</div>
  );
}

export function AirlineDetails({
  readonly,
  airline,
  onChange
}: {
  readonly: boolean,
  airline: CodeAirLine,
  onChange: (change: Partial<CodeAirLine>) => void,
}) {
  const systemSettings = useSystemSettings();
  const countries = systemSettings.countries ?? [];
  const country:Country|null = !!airline?.country ? (countries.find(x => x.name === airline.country) ?? null) : null;

  function setCountry(c:Country|null) {
    onChange({
      country: c?.name ?? '',
      countryUnCode: c?.unCode ?? ''
    });
  }

  useEffect(() => {
    if (readonly) {
      return;
    } 
    
    const focusElement = document.getElementById('airline-name');
    focusElement?.focus();
  }, [readonly]);

  return (
    <div>
      <div style={{float: 'left', marginRight: '10px', width: 'calc(25% - 10px)'}}>
        <RowSpacer />
        <InputControlRow label={<span>Airline Name <span style={{color: 'red'}}>*</span></span>}>
          <TextBox name='airline-name' textId='airline-name' maxLength={65} disabled={readonly} value={airline?.name || ''} onChange={e =>
            onChange({name: e.target.value.toUpperCase()})} />
        </InputControlRow>

        <InputControlRow label="Country (UN Code)">
          <LeftRightSplit
            leftSize="60px"
            rightSize="calc(100% - 60px)"
            left={
              <CountryIsoCodeDropDown
                maxLength={55}
                name='airline-counrty'
                disabled={readonly}
                value={country}
                onChange={setCountry}
                items={countries}
              />
            }
            right={<TextBox name='airline-countrycode' disabled value={airline?.country ?? ''} />}
          />
        </InputControlRow>

        <LeftRightSplit
          left={
            <div style={{marginRight: '10px'}}>
              <InputControlRow label={<span>Prefix <span style={{color: 'red'}}>*</span></span>}>
                <TextBox name='airline-prefix' maxLength={3} disabled={readonly} value={airline?.prefix || ''} onChange={e => onChange({prefix: e.target.value})} />
              </InputControlRow>
            </div>
          }
          right={
            <div style={{marginLeft: '10px'}}>
            </div>
          }
        />

        <LeftRightSplit
          left={
            <div style={{marginRight: '10px'}}>
              <InputControlRow label={<span>Abbr2 <span style={{color: 'red'}}>*</span></span>}>
                <TextBox name='airline-abbr2' maxLength={2} disabled={readonly} value={airline?.abbr2 || ''} onChange={e => onChange({abbr2: e.target.value})} />
              </InputControlRow>
            </div>
          }
          right={
            <div style={{marginLeft: '10px'}}>
              <InputControlRow label="Abbr3">
                <TextBox name='airline-abbr3' maxLength={3} disabled={readonly} value={airline?.abbr3 || ''} onChange={e => onChange({abbr3: e.target.value})} />
              </InputControlRow>
            </div>
          }
        />

        <LeftRightSplit
          left={
            <div style={{marginRight: '10px'}}>
              <InputControlRow label="Next AWB #">
                <TextBox name='airline-nextblno' maxLength={7} disabled={readonly} value={airline?.nextBLno || ''} onChange={e => onChange({nextBLno: e.target.value})} />
              </InputControlRow>
            </div>
          }
          right={
            <div style={{marginLeft: '10px'}}>
              <InputControlRow label="End AWB #">
                <TextBox name='airline-endblno' maxLength={7} disabled={readonly} value={airline?.endBLno || ''} onChange={e => onChange({endBLno: e.target.value})} />
              </InputControlRow>
            </div>
          }
        />

        <LeftRightSplit
          left={
            <div style={{marginRight: '10px'}}>
              <InputControlRow label="Update Date">
                <div style={{position: 'relative', top: '-2px'}}>
                  <DateTime name='airline-u2Date' disabled value={airline?.u2Date} />
                </div>
              </InputControlRow>
            </div>
          }
          right={
            <div style={{marginLeft: '10px', position: 'relative', top: '-2px'}}>
              <InputControlRow label="By">
                <TextBox name='airline-u2Id' disabled value={airline?.u2Id || ''} />
              </InputControlRow>
            </div>
          }
        />
      </div>
      <div style={{float: 'left', marginLeft: '10px', marginRight: '10px', width: 'calc(25% - 10px)'}}>
        <RowSpacer />
        <InputControlRow label="Freight Vendor">
          <CompanyListExLookupField
            maxLength={50}
            name='airline-freightVendor'
            //initialValue={airline?.freightVendor?.shortName ?? ''}
            initialValue={airline?.billTo ?? ''}
            //onTextChange={shortName => onChange({ freightVendor: { id: 0, shortName }})}
            onTextChange={shortName => onChange({ billTo: shortName, companyID: 0 })}
            //onSelect={freightVendor => onChange({freightVendor})}
            onSelect={freightVendor =>{ const company = freightVendor as LookupCompany; onChange({companyID: company?.id ?? 0, billTo: company?.shortName ?? '' })}}
            disabled={readonly}
          />
        </InputControlRow>

        <InputControlRow label="Account #">
          <TextBox name='airline-accountNo' maxLength={15} disabled={readonly} value={airline?.accountNo} onChange={e => onChange({accountNo: e.target.value})} />
        </InputControlRow>
      </div>
      <div style={{float: 'left', marginLeft: '10px', width: 'calc(50% - 20px)'}}>
        <RowSpacer />
        <LeftRightSplit
          leftSize="33%"
          rightSize="64%"
          left={
            <div style={{marginRight: '10px'}}>
              <InputControlRow label="FSC Rate">
                <Float name='airline-aefscrate' decimals={2} min={0} max={999999999999.99} disabled={readonly} value={airline?.aefscRate} onChange={fscRate => onChange({aefscRate: fscRate ?? 0})} />
              </InputControlRow>
            </div>
          }
          right={
            <div style={{marginLeft: '10px'}}>
              <LeftRightSplit
                left={
                <div style={{marginRight: '10px'}}>
                  <InputControlRow label="Minimum ($)">
                    <Float name='airline-aefscmin' decimals={2} min={0} max={999999999999.99} disabled={readonly} value={airline?.aefscMin} onChange={fscRateMin => onChange({aefscMin: fscRateMin ?? 0})} />
                  </InputControlRow>
                </div>
                }
                right={
                  <div id='airline-fscgwt' style={{marginLeft: '10px'}}>
                    <InputControlRow label=" ">
                        <CheckBox name='airline-fscgWt' focusId='airline-fscgwt' label='Gross Weight' disabled={readonly} value={!!airline?.fscgWt} onChange={fscRateGross => onChange({fscgWt: fscRateGross})} />
                      </InputControlRow>
                  </div>
                }
              />
            </div>
          }
        />
        <LeftRightSplit
          leftSize="33%"
          rightSize="64%"
          left={
            <div style={{marginRight: '10px'}}>
              <InputControlRow label="SSC Rate">
                <Float name='airline-aesscrate' decimals={2} min={0} max={999999999999.99} disabled={readonly} value={airline?.aesscRate} onChange={sscRate => onChange({aesscRate: sscRate ?? 0})} />
              </InputControlRow>
            </div>
          }
          right={
            <div style={{marginLeft: '10px'}}>
              <LeftRightSplit
                left={
                <div style={{marginRight: '10px'}}>
                  <InputControlRow label="Minimum ($)">
                    <Float name='airline-aesscmin' decimals={2} min={0} max={999999999999.99} disabled={readonly} value={airline?.aesscMin} onChange={sscRateMin => onChange({aesscMin: sscRateMin ?? 0})} />
                  </InputControlRow>
                </div>
                }
                right={
                  <div id='airline-sscgwt' style={{marginLeft: '10px'}}>
                    <InputControlRow label=" ">
                        <CheckBox name='airline-sscgwt' focusId='airline-sscgwt' label='Gross Weight' disabled={readonly} value={!!airline?.sscgWt} onChange={sscRateGross => onChange({sscgWt: sscRateGross??0})} />
                      </InputControlRow>
                  </div>
                }
              />
            </div>
          }
        />
        <LeftRightSplit
          leftSize="33%"
          rightSize="64%"
          left={
            <div style={{marginRight: '10px'}}>
              <InputControlRow label="TSA Screen Charge Rate">
                <Float name='airline-tsaRate' decimals={2} min={0} max={999999999999.99} disabled={readonly} value={airline?.tsaRate} onChange={tsaScreenChargeRate => onChange({tsaRate: tsaScreenChargeRate??0})} />
              </InputControlRow>
            </div>
          }
          right={
            <div style={{marginLeft: '10px'}}>
              <LeftRightSplit
                left={
                <div style={{marginRight: '10px'}}>
                  <InputControlRow label="Minimum ($)">
                    <Float name='airline-tsaMin' decimals={2} min={0} max={999999999999.99} disabled={readonly} value={airline?.tsaMin} onChange={tsaScreenChargeRateMin => onChange({tsaMin: tsaScreenChargeRateMin??0})} />
                  </InputControlRow>
                </div>
                }
                right={
                  <div id='airline-tsagWt' style={{marginLeft: '10px'}}>
                    <InputControlRow label=" ">
                        <CheckBox name='airline-tsagWt' focusId='airline-tsagWt' label='Gross Weight' disabled={readonly} value={!!airline?.tsagWt} onChange={tsaScreenChargeRateGross => onChange({tsagWt: tsaScreenChargeRateGross??0})}
                        onTabNext={() => {
                          document.getElementById('airline-name')?.focus();
                          return true;
                        }} />
                      </InputControlRow>
                  </div>
                }
              />
            </div>
          }
        />
      </div>
    </div>
  );
}

function formatData(data: any, pageNum: number, pageSize: number, pagePerBatch:number) {
  if (!data) return;
  
  const blockStartPage = pageNum - ((pageNum - 1) % pagePerBatch);
  
  return data.map((d: any, i: number) => {
    const indicator = (blockStartPage - 1) * pageSize + (i + 1);
    return { ...d, indicator };
  });
}

function Airlines({
  componentIndex
}: {
  componentIndex: number
}) {
  useFunction({invokers: {
    ifmsReferenceFilesSave: save,
    ifmsReferenceFilesCancel: cancelOperation,
  }});
  const pageSize: number = 25;
  const pagePerBatch: number = 10;

  useFunctionKeys({ onEsc: cancelOperation, onSave: handleSaveAndClose });
  
  const appContext = useContext(ApplicationContext);
  const refContext = useContext(IFmsReferenceFilesContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
  const { selectTableRow, tableNavigationCleanup } = useTableNavigation('codes-airline-list', onRowSelect, onRowDoubleSelect, ['name'], false, false, false);
  const [pageNum, setPageNum, deletePageNum, pageNumRef] = useTypedFastStorage<number>(`codes-airline-list-pageum`, 1);

  const [loading, setLoading] = useState(false);
  const [, setHasChanges] = useFastStorage(`ifms-referenceFiles-haschanges`, false);
  const [data, _setData, removeData, dataRef] = useFastStorage(`ifms-referenceFiles-airline-list`, []);
  const [selectedRow, setSelectedRow] = useState(-1);

  const [airline, _setAirline, removeAirlineFromStorage] = useTypedFastStorage<CodeAirLine>(`ifms-referenceFiles-airline-editable`, null);
  const { setCleanState, dirtyCleanup } = useDirtyCheck(`ifms-referenceFiles-airline-editable`, ['edit', 'view']);
  const [orderBy, setOrderBy] = useTypedFastStorage<ListOrderBy>(`ifms-referenceFiles-airline-orderby`, getDefaultOrderBy());

  const totalDataCountRef = useRef(0);
  const headerOrderByText = useRef('');

  const listSec = findSecurityTask(appContext,  FmsSecTask.CodeAirLine);
  const tableHeight = 522;

  const { FilterButton, search } = useFilterBar(
    codesAirlineList,
    undefined, 
    getDefaultOrderBy(),
    String(componentIndex),
    false,
    setPageNum,
    {
      pageSize: pageSize,
      pagePerBatch: pagePerBatch,
    },
    setOrderBy,
  );

  function handleSaveAndClose() {
    const saveBtn = document.getElementById('detail-save-button');
    if (saveBtn) {
      saveBtn.focus();
      const modals = document.querySelectorAll('[class^="ReactModal__Content"]');
      const modalsWithoutSaveBtn = Array.from(modals).filter(modal => { return !modal.contains(saveBtn); });
      if (modalsWithoutSaveBtn.length <= 0)
        saveBtn.click();
    }
  }

  function getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'name',
      orderByAscending: true,
    };
  }

  function setAirline(a:Partial<CodeAirLine>, skipDirty:boolean = false) {
    if (!skipDirty) {
      setHasChanges(true);
    }
    _setAirline(a)
  }

  function setData(d:Array<CodeAirLine>) {
    removeData();
    _setData(d);
  }

  async function fetchData(api: any, pageNum: number, forceCallDb: boolean, newItem: any = undefined) {
    setLoading(true);

    const prevPageNum = pageNumRef.current;
    const prevBlockStartPage = prevPageNum ? prevPageNum - ((prevPageNum - 1) % search.pagePerBatch) : null;
    const prevBlockNum = prevBlockStartPage ? Math.floor((prevBlockStartPage - 1) / search.pagePerBatch) : null;

    setPageNum(pageNum);

    const blockStartPage = pageNum - ((pageNum - 1) % search.pagePerBatch);
    const blockNum = Math.floor((blockStartPage - 1) / search.pagePerBatch);

    const isSameBlock = prevBlockNum === blockNum;
    const searchWithHeaderOrder = {
      ...search,
      headerOrderBy: headerOrderByText.current,
    };

    let entities: any[] = [];

    if (!isSameBlock || forceCallDb) {
      const apiResult = await api.getListItems(JobNames.AirLineEx,
        'CodeAirlineListItem',
        CodeAirlineListItem.calculateSearchParameters(searchWithHeaderOrder, CodeAirlineListItem.MapSearchFields),
        blockStartPage,
        0,
        search.pageSize,
        search.pagePerBatch);

      entities = apiResult && apiResult.entities ? apiResult.entities : [];

      if (entities.length > 0) {
        if (newItem) {
          const filteredEntities = entities.filter(e => e.id !== newItem.id);

          let merged = [newItem, ...filteredEntities];

          const maxCount = search.pageSize * search.pagePerBatch;
          if (merged.length > maxCount) {
            merged = merged.slice(0, maxCount);
          }

          entities = merged;
        }
      }
      totalDataCountRef.current = apiResult?.totalCount ?? 0;
      setData(formatData(entities, pageNum, search.pageSize, search.pagePerBatch));
    }

    setHasChanges(false);
    setLoading(false);
  }

  useEffect(() => {
    if (isFilterObjectEmpty(search) && search.version === 1) reload();
    
    return () => {
      removeData();
      tableNavigationCleanup();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (loading || (isFilterObjectEmpty(search) && search.version === 1)) {
      return;
    }
    reload(true);
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search]
  );

  useEffect(() => {
    if (dataRef.current?.length > 0 && !loading && pageNum) {
      const blockStartPage = pageNum - ((pageNum - 1) % pagePerBatch);
      const indexOffset = (pageNum - blockStartPage) * pageSize;
      setTimeout(() => {
        selectTableRow(indexOffset, true);
        onRowSelect(indexOffset);
      }, 10);
    }
  }, [loading, pageNum]);
  
  function updateHeaderOrderByText(orderBy: string, orderByAscending: boolean) {
    if (!orderBy) return;
    const orderStr = orderByAscending
      ? `${orderBy} ASC`
      : `${orderBy} DESC`;
    headerOrderByText.current = orderStr;
  }

  async function reload(clearHeaderOrder: boolean = true) {
    if (clearHeaderOrder) {
      if (isFilterObjectEmpty(search)) {
        headerOrderByText.current = '';
        setOrderBy(getDefaultOrderBy());
      }
      else {
        updateHeaderOrderByText(search.orderBy, search.orderByAscending);
        setOrderBy({ orderBy: search.orderBy, orderByAscending: search.orderByAscending });
      }
    }

    await fetchData(appContext.api, 1, true);
  }

  async function onRowSelect(rowIndex: number){
    setSelectedRow(!isNaN(rowIndex) ? rowIndex : -1);
  }

  async function onRowDoubleSelect(rowIndex: number) {
    if (!dataRef || !dataRef.current) {
      return;
    }

    //<pt220817: portal - load from backend
    //setAirline({..._.cloneDeep(dataRef.current[rowIndex]), edit: true, view: true, editIndex: rowIndex}, true);

    const apiResult: ApiResult<CodeAirLine> = await appContext.api.getJobDetail(JobNames.AirLineEx,
      { id: dataRef.current[rowIndex].id });
    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(<p>{apiResult?.message ?? constants.LOAD_ERROR_MESSAGE}</p>);
      return;
    }

    const objAirline = { ...apiResult.result, edit: true, view: true, editIndex: rowIndex };
    setCleanState(objAirline);
    const tEntity = new CodeAirLine(objAirline);
    tEntity.trackable = true;
    setAirline(tEntity, true);
    //pt220817>
  }

  function onHeaderClick(header:any) {
    if (!pageNum) {
      return;
    }
   
    if (!orderBy || !orderBy.orderBy) return;

    if (header && header.id !== 'indicator') {
      if (header.id === orderBy.orderBy) {
        setOrderBy({orderByAscending: !orderBy.orderByAscending});
      } else {
        setOrderBy({orderBy: header.id, orderByAscending: true});
      }

      let newOrderStr = `${header.id} ASC`;

      if (orderBy && orderBy.orderBy === header.id) {
        newOrderStr = orderBy.orderByAscending ? `${header.id} ASC` : `${header.id} DESC`;
      }

      headerOrderByText.current = newOrderStr;
      fetchData(appContext.api, pageNum, true);
    }
  }

  async function setAirlineDetail(isView: boolean){
    const apiResult:ApiResult<CodeAirLine> = await appContext.api.getJobDetail(JobNames.AirLineEx,
      {id: dataRef.current[selectedRow].id });
    if(!apiResult || !apiResult.success) {
      await showGlobalOkDialog(<p>{apiResult?.message ?? constants.LOAD_ERROR_MESSAGE}</p>);
      return;
    }

    const objAir = {...apiResult.result, edit: true, view: isView, editIndex: selectedRow};
    setCleanState(objAir);
    const tEntity = new CodeAirLine(objAir);
    tEntity.trackable = true;
    setAirline(tEntity, true);
  }

  function selectAirlineForNew() {
    const newCodeModel = new CodeAirLine();   
    setAirline(newCodeModel, false);
    setCleanState(newCodeModel);
    
    setAirline({
      edit : false,
      view: false,
    });
  }
  
  async function selectAirlineForEdit(){
    await setAirlineDetail(false);
  }
  
  async function selectAirlineForView(){
    await setAirlineDetail(true);
  }

  async function selectAirlineForDelete(){
    const doConfirm = await showGlobalYesNoDialog(
      <div>
        <h3>Do you want to Delete?</h3>
      </div>
    , false, 0, true, 'warning');
    if(doConfirm){
      const apiResult = await appContext.api.deleteJobDetail(JobNames.AirLineEx, { id:dataRef.current[selectedRow].id , pimsLinkId: 0 });
      if (apiResult && apiResult.success) {
        await reload();
      } else
      showGlobalOkDialog(<p>{"Cannot delete"}</p>);
    }else
    return;
  }

  function cancelOperation() {
    const filterNode = document.getElementById('ifms-list-screen-filter');
    const channel = postal.channel('ifms-filter');

    if (!filterNode) {
      setHasChanges(false);
      removeAirlineFromStorage();
    } else {
      channel.publish('ifms-filter.dismiss');
    }
    return true;
  }

  async function save() {
    if (!airline || !pageNum) {
      return;
    }
    else {
      let errorMsg = '';
      if (_.isEmpty(airline.name))
        errorMsg = appendLine(errorMsg, 'Please enter the Airline Name');
      if (_.isEmpty(airline.prefix))
        errorMsg = appendLine(errorMsg, 'Please enter the Prefix');
      if (_.isEmpty(airline.abbr2))
        errorMsg = appendLine(errorMsg, 'Please enter the Abbr2');

      if (errorMsg.length > 0) {
        await showGlobalOkDialog(
          <div>
            <h3>Please check following information. (Specify the missing entries.)</h3>
            {errorMsg}
          </div>
        );
        return;
      }

      if (airline.name.length > 0) {
        if (await appContext.api.checkupDuplicateFieldValueIsDuplicate("T_CODEAIRLINE", "F_Name", airline.name, 'F_Id', airline.id)) {
          errorMsg = appendLine(errorMsg, `The Name '${airline.name}' is already taken`);
        }
      }
      if (airline.prefix.length < 3)
        errorMsg = appendLine(errorMsg, 'Prefix must be 3 digits');
      else {
        if (await appContext.api.checkupDuplicateFieldValueIsDuplicate("T_CODEAIRLINE", "F_Prefix", airline.prefix, 'F_Id', airline.id)) {
          errorMsg = appendLine(errorMsg, `The prefix '${airline.prefix}' is already taken`);
        }
      }
      if (airline.abbr2.length < 2)
        errorMsg = appendLine(errorMsg, 'Abbreviation must be 2 characters');
      else {
        if (await appContext.api.checkupDuplicateFieldValueIsDuplicate("T_CODEAIRLINE", "F_Abbr", airline.abbr2, 'F_Id', airline.id)) {
          errorMsg = appendLine(errorMsg, `The abbreviation '${airline.abbr2}' is already taken`);
        }
      }
      if (errorMsg.length > 0) {
        await showGlobalOkDialog(
          <div>
            <h3>Please check following information.</h3>
            {errorMsg}
          </div>
        );
        return;
      }
    }

    const tEntity = new CodeAirLine(airline);
    tEntity.trackable = true;
    tEntity.u2Date = now();
    tEntity.u2Id = appContext.user?.id ?? '';
    setAirline(tEntity, false);
    
    const sqlInfoList = getSQLInfoList(airline as TrackableEntity);
    const sqlInfo = sqlInfoList.find(x => stringsEqualIgnoreCase(x.tableName, 't_aotherinfo'));
    if(sqlInfo) {
      sqlInfo.whereFields.forEach(f => {
        if(f.value === '@AirCodeId')
          f.value = airline.id;
      });
    }

    const apiResult:ApiResult<CodeAirLineResult> = await appContext.api.saveJobDetail(
      JobNames.AirLineEx, 
      sqlInfoList,
    );

    if(!apiResult || !apiResult.success) {
      await showGlobalOkDialog(<p>{apiResult?.message ?? constants.SAVE_ERROR_MESSAGE}</p>);
      return;
    }else{
      await showGlobalOkDialog(<p>The data has been saved</p>);

      if(apiResult.result){
        const insresult = apiResult.result.newId;
        if (insresult) {
          const newItem: any = {
            id: insresult,
            name: airline.name,
            country: airline.country,
            prefix: airline.prefix,
            abbr: airline.abbr2,
            abbr3: airline.abbr3,
            nextblNo: airline.nextBLno,
            endblNo: airline.endBLno,            
            billTo: airline.billTo,
            aefscRate: airline.aefscRate,
            aefscMin: airline.aefscMin,
            aesscRate: airline.aesscRate,
            aesscMin: airline.aesscMin,
          };
          await fetchData(appContext.api, 1, true, newItem);
        }
      }
      else {
        const editItem = data.find((x: any) => x.id === airline.id);
        editItem.name = airline.name;
        editItem.country = airline.country;
        editItem.prefix = airline.prefix;
        editItem.abbr = airline.abbr2;
        editItem.abbr3 = airline.abbr3;
        editItem.nextblNo = airline.nextBLno;
        editItem.endblNo = airline.endBLno;            
        editItem.billTo = airline.billTo;
        editItem.aefscRate = airline.aefscRate;
        editItem.aefscMin = airline.aefscMin;
        editItem.aesscRate = airline.aesscRate;
        editItem.aesscMin = airline.aesscMin;

        const insIndex = data.findIndex((x:any) => x.id === airline.id);
        const newList = _.cloneDeep(data);
        newList.splice(insIndex, 1, editItem);
        setData(formatData(newList, pageNum, pageSize, pagePerBatch));

        selectTableRow(insIndex);
      } 
    }

    cancelOperation();
    dirtyCleanup(); //<pt220817>
  }

  const tableOptions:ApplicableTableSettings = {
    customTableId: 'codes-airline-list',
    selectable: true,
    tableName: 'codesAirlineList',
    ...codesAirlineList,
    height: `${tableHeight}px`,
    data: (data ?? []).map((x: any, i:number) => ({ ...x })),
    rowRenderers: [
      { accessor: 'indicator', Cell: TextRenderer },
      { accessor: 'name', Cell: TextAlignLeftRenderer },
      { accessor: 'country', Cell: TextRenderer },
      { accessor: 'prefix', Cell: TextRenderer },
      { accessor: 'abbr', Cell: TextRenderer },
      { accessor: 'abbr3', Cell: TextRenderer },
      { accessor: 'nextblNo', Cell: TextAlignRightRenderer },
      { accessor: 'endblNo', Cell: TextAlignRightRenderer },
      { accessor: 'billTo', Cell: TextAlignLeftRenderer },
      { accessor: 'aefscRate', Cell: FloatRenderer },
      { accessor: 'aefscMin', Cell: FloatRenderer },
      { accessor: 'aesscRate', Cell: FloatRenderer },
      { accessor: 'aesscMin', Cell: FloatRenderer },
      { accessor: 'updateDate', Cell: DateTimeRenderer },
    ],
    orderBy: [orderBy],
    onHeaderClick,
    paging: {
      pageSize: pageSize,
      pageCount: Math.ceil(totalDataCountRef.current / pageSize),
      pagePerBatch: pagePerBatch,
      onPageSelect: (page: number) => {
        fetchData(appContext.api, page, false);
      },
      totalDataCount: totalDataCountRef.current,
      pageNum,
      labelStyle: {
        hide: false,
        isFilterEmpty: isFilterObjectEmpty(search),
        emptyLabel: 'Total',
      },
    },
  };

  //const newEnabled = airline === null;
  const hasSelectedRow = !isNaN(selectedRow) && !(selectedRow < 0);
  const canBeAdd = listSec?.grantAdd;
  const canBeEdit = hasSelectedRow && listSec?.grantEdit;
  const canBeDeleted = hasSelectedRow && listSec?.grantDel;

  return (
    <div style={{ marginTop: '40px' }}>
      <Portal node={document && document.getElementById('settings-command-placeholder')}>
        <div style={{ display: 'flex', width: '100px' }}>
          <div style={{ margin: 'auto' }}></div>
          <div className="ifms-common-bar-element-center" >
            <CloseButton onlyIcon name='settings-close' mode='light' onClick={refContext.cancel} />
          </div>
        </div>
      </Portal>
      <div style={{ display: 'flex', width: '1236px' }}>
        <div style={{marginLeft: 'auto'}}></div>
        <div style={{ display:'flex', position: 'relative', top: '-4px' }}>
          <div style={{ display: 'none' }}>
            <FilterButton /> &nbsp;&nbsp;
          </div>
          <AddIconButton3 name='codes-airline-add-button' disabled={!canBeAdd} onClick={selectAirlineForNew}/>&nbsp;&nbsp;
          <EditIconButton3 name='codes-airline-edit-button' disabled={!canBeEdit} onClick={selectAirlineForEdit}/>&nbsp;&nbsp;
          <ViewIconButton3 name='codes-airline-view-button' disabled={!hasSelectedRow} onClick={selectAirlineForView} iconHeight='26px' />&nbsp;&nbsp;
          <DeleteIconButton3 name='codes-airline-delete-button' disabled={!canBeDeleted} onClick={selectAirlineForDelete}/>&nbsp;
          {/* <BlackInverseButton name='list-screen-refresh' size="large" disabled={loading} onClick={() => { reload() }} data-tooltip="Refresh" >
            <FontAwesomeIcon color='#47C83E' icon={faSync} />
          </BlackInverseButton> */}
          <RefreshIconButton2 name='list-screen-refresh' onClick={() => { reload() }} disabled={loading} data-tooltip="Refresh"/>
        </div>
      </div>
      <div style={{ width: '1236px' }}>
        <SmartTable {...tableOptions} />
      </div>
      <RowSpacer />

      <IfmsModal isOpen={!!airline} title={airline && airline.edit ? (airline.view ? 'Airline View' : 'Airline Edit') : 'Airline New'} onCancel={cancelOperation}>
        {!!airline &&
          <div>
            <div style={{display: 'flex'}}>
              <div style={{marginLeft: 'auto'}}></div>
              <div className="ifms-common-bar-element ifms-right-separator-light-gray">
                {airline.view && listSec?.grantEdit &&
                  <EditIconButton3 name='airline-detail-edit-button' onClick={() => setAirline({view: false}, true)}/>
                }
                {!airline.view &&
                  <SecondaryButton id='detail-save-button' name='airline-detail-save-button' size="regular" onClick={save}><span style={{ textDecoration: 'underline' }}>S</span>ave</SecondaryButton>
                }
                &nbsp;
              </div>
              <div className="ifms-common-bar-element">
                <CloseButton onlyIcon name='airline-detail-close-button' onClick={cancelOperation} />
              </div>
            </div>
            <AirlineDetails readonly={airline.view} airline={airline} onChange={setAirline} />
            <div style={{clear: 'both'}}></div>
          </div>
        }
      </IfmsModal>
    </div>
  );
}

export default Airlines;
